import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../utils/axios';
import { Category } from '../../types';

const validationSchema = Yup.object({
  name: Yup.string().required('El nombre es requerido'),
  description: Yup.string(),
  evaluationFields: Yup.array().of(Yup.string())
});

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [newField, setNewField] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/categories');
      setCategories(response.data);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      setError(error.response?.data?.message || 'Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category?: Category) => {
    setSelectedCategory(category || null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCategory(null);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (selectedCategory) {
        await axiosInstance.put(`/categories/${selectedCategory.id}`, values);
      } else {
        await axiosInstance.post('/categories', values);
      }
      fetchCategories();
      handleCloseDialog();
      setError(null);
    } catch (error: any) {
      console.error('Error saving category:', error);
      setError(error.response?.data?.message || 'Error al guardar la categoría');
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta categoría?')) {
      try {
        await axiosInstance.delete(`/categories/${categoryId}`);
        fetchCategories();
        setError(null);
      } catch (error: any) {
        console.error('Error deleting category:', error);
        setError(error.response?.data?.message || 'Error al eliminar la categoría');
      }
    }
  };

  const handleAddField = (setFieldValue: (field: string, value: any) => void, values: any) => {
    if (newField.trim()) {
      setFieldValue('evaluationFields', [...(values.evaluationFields || []), newField.trim()]);
      setNewField('');
    }
  };

  const handleRemoveField = (setFieldValue: (field: string, value: any) => void, values: any, index: number) => {
    const newFields = [...(values.evaluationFields || [])];
    newFields.splice(index, 1);
    setFieldValue('evaluationFields', newFields);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Categorías</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Categoría
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        {categories.map((category) => (
          <Box
            key={category.id}
            sx={{
              p: 2,
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{category.name}</Typography>
              <Box>
                <IconButton onClick={() => handleOpenDialog(category)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDelete(category.id)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
            {category.description && (
              <Typography color="textSecondary" sx={{ mt: 1 }}>
                {category.description}
              </Typography>
            )}
            {category.evaluationFields && category.evaluationFields.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Campos de evaluación:
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {category.evaluationFields.map((field, index) => (
                    <Chip key={index} label={field} size="small" />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedCategory ? 'Editar Categoría' : 'Nueva Categoría'}
        </DialogTitle>
        <Formik
          initialValues={
            selectedCategory
              ? {
                  name: selectedCategory.name,
                  description: selectedCategory.description || '',
                  evaluationFields: selectedCategory.evaluationFields || []
                }
              : {
                  name: '',
                  description: '',
                  evaluationFields: []
                }
          }
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form>
              <DialogContent>
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="name"
                  label="Nombre"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="description"
                  label="Descripción"
                  multiline
                  rows={3}
                />
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Campos de evaluación
                  </Typography>
                  <Box display="flex" gap={1} mb={2}>
                    <TextField
                      fullWidth
                      size="small"
                      value={newField}
                      onChange={(e) => setNewField(e.target.value)}
                      placeholder="Nuevo campo de evaluación"
                    />
                    <Button
                      variant="outlined"
                      onClick={() => handleAddField(setFieldValue, values)}
                    >
                      Agregar
                    </Button>
                  </Box>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {values.evaluationFields?.map((field: string, index: number) => (
                      <Chip
                        key={index}
                        label={field}
                        onDelete={() => handleRemoveField(setFieldValue, values, index)}
                      />
                    ))}
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>Cancelar</Button>
                <Button type="submit" variant="contained" color="primary">
                  Guardar
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default Categories; 